import React, { useRef, useState } from 'react';
import {
  Button,
  ButtonColor,
} from '../../../../components/uikit/buttons/Button.tsx';
import clsx from 'clsx';
import styles from '../Repairs.module.scss';
import { CustomModal } from '../../../../components/uikit/modal/CustomModal.tsx';
import { useScopedTranslation } from '../../../../application/localization/useScopedTranslation.ts';
import { Input } from '../../../../components/uikit/inputs/Input.tsx';
import { Checkbox } from '../../../../components/uikit/inputs/Checkbox.tsx';
import { Loading } from '../../../../components/uikit/suspense/Loading.tsx';
import { RepairsResponseDto } from '../../../../services/api/api-client.types.ts';
import { formatDate } from '../../../../helpers/abas-helper.ts';
import Placeholder from 'assets/img/notifications-placeholder.png';
import { FeatureFlags } from '../../../../application/constants/env-variables.ts';
import { Links } from '../../../../application/constants/links.ts';
import { useNavigate } from 'react-router-dom';

type OwnProps = {
  isModalVisible: boolean;
  isLoading: boolean;
  changeMultiPDFModalVisibility: (isVisible: boolean) => void;
  changePDFInputValue: (value: string) => void;
  generatePDF: (incomingNumbers: string[]) => Promise<void>;
  isDataEmpty: boolean;
  data?: RepairsResponseDto;
};

export const MultiPdfModal = (props: OwnProps) => {
  const {
    isModalVisible,
    changeMultiPDFModalVisibility,
    isLoading,
    data,
    generatePDF,
    isDataEmpty,
    changePDFInputValue,
  } = props;

  const navigate = useNavigate();

  const [selectedIncomingNumbers, setSelectedIncomingNumbers] = useState<
    string[]
  >([]);

  const timerId = useRef<NodeJS.Timeout>(null);

  const i18n = useScopedTranslation('Page.Repairs');

  const removeSelectedIncomingNumber = (incomingValue: string) => {
    setSelectedIncomingNumbers((prev) =>
      prev.filter((r) => r !== incomingValue),
    );
  };

  const filterList = (newValue: string) => {
    if (timerId.current) {
      clearTimeout(timerId.current);
    }

    timerId.current = setTimeout(() => changePDFInputValue(newValue), 700);
  };

  const handleClose = () => {
    changePDFInputValue('');
    changeMultiPDFModalVisibility(false);
    setSelectedIncomingNumbers([]);
  };

  if (!isModalVisible) {
    return null;
  }

  return (
    <CustomModal
      isOpen={true}
      onSubmit={() => {
        void generatePDF(selectedIncomingNumbers);
        handleClose();
      }}
      onCancel={handleClose}
      cancelButtonText={i18n.t('cancel')}
      submitButtonText={i18n.t('create_pdf')}
      submitButtonType={ButtonColor.Primary}
      cancelButtonType={ButtonColor.Secondary}
      titleText={i18n.t('generate_multi_pdf')}
      containerClassName={
        isDataEmpty ? styles.pdfModalPlaceholder : styles.pdfModal
      }
      contentClassName={styles.pdfModalContent}
      buttonContainerClassName={styles.buttonModalContainer}
      doNotCloseOnOutsideClick={true}
      isDisabled={!selectedIncomingNumbers.length}
      textClassName={styles.listParent}
      hideButtons={isDataEmpty}
    >
      <Loading
        loading={isLoading}
        wrapperClassName={styles.loader}
        className={styles.absoluteContainer}
      />
      {isDataEmpty ? (
        <div className={styles.placeholderContainer}>
          <img src={Placeholder} className={styles.height168} />
          <div className={clsx(styles.bold, styles.placeholderTitle)}>
            {i18n.t('no_entered_requests')}
          </div>
          <div className={clsx(styles.text, styles.textCenter)}>
            {i18n.t('no_entered_requests_description')}
          </div>
          {FeatureFlags.isCreateRepairRequestEnabled() && (
            <Button
              title={i18n.t('create_repair_request')}
              onClick={() => {
                handleClose();
                void navigate(Links.Authorized.CreateRepairRequest.route);
              }}
              className={styles.marginTop24}
            />
          )}
        </div>
      ) : (
        <>
          <div
            className={clsx(
              styles.greyText,
              styles.marginBottom8,
              styles.paddingRight24,
            )}
          >
            {i18n.t('modal_description')}
          </div>
          <Input
            placeholder={i18n.t('search_requests')}
            containerClassName={clsx(
              styles.marginBottom24,
              styles.paddingRight24,
              styles.borderBox,
            )}
            fullWidth={true}
            onChange={(e) => filterList(e.target.value)}
          />
          <Checkbox
            actionText={i18n.t('select_all')}
            containerClassName={styles.marginBottom12}
            halfChecked={!!selectedIncomingNumbers.length}
            isChecked={
              selectedIncomingNumbers.length === data?.countOfRecords &&
              selectedIncomingNumbers.length > 0
            }
            onChange={(e) => {
              if (selectedIncomingNumbers.length === data?.countOfRecords) {
                setSelectedIncomingNumbers([]);
              } else {
                setSelectedIncomingNumbers(
                  data?.records.map((item) => item.order) ?? [],
                );
              }
            }}
          />
          <div className={styles.divider} />
          <div className={styles.list}>
            {data?.records.map((item) => (
              <div className={styles.deviceItem} key={item.order}>
                <div className={clsx(styles.rowContainer, styles.spaceBetween)}>
                  <Checkbox
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedIncomingNumbers((prev) => [
                          ...prev,
                          item.order,
                        ]);
                      } else {
                        removeSelectedIncomingNumber(item.order);
                      }
                    }}
                    isChecked={selectedIncomingNumbers.includes(item.order)}
                    actionText={item.order}
                    actionTextClassName={styles.bold}
                  />
                  <div className={styles.greyText}>{formatDate(item.from)}</div>
                </div>
                <div
                  className={clsx(
                    styles.columnContainer,
                    styles.rowGap4,
                    styles.marginLeft28,
                  )}
                >
                  <div className={clsx(styles.rowContainer, styles.columnGap4)}>
                    <div className={styles.greyText}>{i18n.t('device')}</div>
                    <div className={styles.text}>{item.name}</div>
                  </div>
                  <div className={clsx(styles.rowContainer, styles.columnGap4)}>
                    <div className={styles.greyText}>{i18n.t('sn_modal')}</div>
                    <div className={styles.text}>{item.sn}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className={styles.buttonDivider} />
        </>
      )}
    </CustomModal>
  );
};
